import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, TextField, Link } from '@mui/material';
import FeedbackService from 'services/FeedbackService';
import BaseModal from 'components/BaseModal';
import RatingStars from '../RatingStars';
import { openMtgChat } from 'utils';
import { useStyles } from './styles';

const Props = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

const initialFormData = {
  score: 0,
  reason: '',
};

export default function FormModal({ open, onClose }) {
  const classes = useStyles();
  const [errorMessage, setErrorMessage] = useState(false);
  const [formData, setFormData] = useState({ ...initialFormData });
  const [errorFields, setErrorFields] = useState([]);
  const feedbackService = new FeedbackService();

  function handleFieldChange(field, data) {
    if (!!data[field] && errorFields.includes(field)) {
      setErrorFields(errorFields.filter((errorField) => errorField !== field));
    }

    setFormData(data);
  }

  function getErrorFields() {
    const fields = [];

    if (formData.reason.length < 2 || formData.reason.length > 255) {
      fields.push('reason');
    }

    return fields;
  }

  function canSubmit() {
    const errors = getErrorFields();

    if (errors.length) {
      setErrorFields(errors);

      return false;
    }

    return true;
  }

  async function handleFeedbackCreate() {
    if (!canSubmit()) {
      return;
    }

    try {
      await feedbackService.create({
        score: formData.score * 2,
        reason: formData.reason,
      });

      setErrorFields([]);
      setFormData({ ...initialFormData });

      onClose(true);
    } catch (e) {
      if (e?.response?.status === 400) {
        setErrorMessage(true);
        onClose(false);
      } else {
        console.error(e);
      }
    }
  }

  useEffect(() => {
    if (!open) {
      setErrorFields([]);
      setFormData({ ...initialFormData });
    }
  }, [open]);

  return (
    <>
      <BaseModal
        size="sm"
        open={errorMessage}
        title={
          <b>
            You already rated Mind The <br /> Graph recently
          </b>
        }
        content={
          <Typography>
            You got to wait a month before giving <br /> new feedbacks.
          </Typography>
        }
        onClose={() => setErrorMessage(false)}
        actionText="OK I understand"
        cancelButton={false}
        onActionClick={() => setErrorMessage(false)}
      />

      <BaseModal
        size="sm"
        open={open}
        title={<b>Send us your review</b>}
        content={
          <Box px={2}>
            <Typography align="left">
              We'd love to hear your feedback. Feel free to tell us what you don't like, give
              suggestions, and let us know what you love about Mind the Graph.
            </Typography>
            <Typography align="left">
              Make sure you use an active email so we can get back to you.
            </Typography>
            <Typography align="left">
              Do you rather talk to someone?{' '}
              <Link className={classes.primaryColor} onClick={openMtgChat} underline="hover">
                Click here
              </Link>
              .
            </Typography>

            <Box mt={3}>
              <TextField
                fullWidth={true}
                multiline={true}
                variant="outlined"
                margin="dense"
                inputProps={{ minRows: 6 }}
                error={errorFields.includes('reason')}
                helperText={
                  errorFields.includes('reason')
                    ? 'Please fill this field correctly'
                    : `${formData.reason.length}/255`
                }
                FormHelperTextProps={{
                  className: !errorFields.includes('reason') ? classes.formHelperText : undefined,
                }}
                label="Message"
                value={formData.reason}
                onChange={({ target: { value } }) =>
                  handleFieldChange('reason', { ...formData, reason: value.slice(0, 255) })
                }
              />
            </Box>

            <Typography variant="h6" gutterBottom={true}>
              Rate our service
            </Typography>

            <RatingStars
              size="big"
              value={formData.score}
              onChange={(value) => handleFieldChange('score', { ...formData, score: value })}
            />
          </Box>
        }
        actionText="Send"
        cancelButton={false}
        onActionClick={handleFeedbackCreate}
        onClose={() => onClose(false)}
      />
    </>
  );
}

FormModal.propTypes = Props;
